/* eslint-disable consistent-return */
import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  getModules,
  getLessons,
  getCurrentModuleSlug,
  getCurrentTopicSlug,
  getCurrentLessonSlug,
  getDischargeSteps,
  getLessonPages
} from './core';
import {
  getCurrentModule,
  getDischargeModuleForCurrentLocale
} from './modules';

const getLessonsWithOrderedPages = createSelector(
  getLessons,
  getLessonPages,
  (lessons, lessonPages) =>
    _.mapValues(lessons, lesson => ({
      ...lesson,
      pages: _.chain(lesson.pages)
        .map(pageId => lessonPages[pageId])
        .sortBy('displayOrder')
        .value()
    }))
);

const getLessonSlugToLessonMap = createSelector(
  getLessons,
  getModules,
  (lessons, modules) =>
    _.reduce(
      lessons,
      (result, lesson) => {
        const module = modules[lesson.moduleId];
        const newResult = { ...result };

        _.set(newResult, `${module.slug}.${lesson.slug}`, lesson);
        return newResult;
      },
      {}
    )
);

const getLessonsForCurrentModule = createSelector(
  getCurrentModule,
  currentModule => {
    if (typeof currentModule !== 'undefined') {
      return (
        _.orderBy(currentModule.lessons, 'menu_order')
      );
    }
  }
);

const getCurrentLesson = createSelector(
  getLessonsForCurrentModule,
  getCurrentLessonSlug,
  getCurrentTopicSlug,
  (currentLessons, lessonSlug, topicSlug) => {
    if (typeof currentLessons !== 'undefined') {
      const thisLesson = currentLessons[parseInt(lessonSlug, 10) - 1];
      const thisTopic = thisLesson && thisLesson.topics[topicSlug];
      return (
        thisTopic && {
          ...thisTopic,
          lastTopic: parseInt(topicSlug, 10) === thisLesson.topics.length - 1
        }
      );
    }
  }
);

const getCurrentDischargeStep = createSelector(
  getDischargeSteps,
  getCurrentModuleSlug,
  (currentLessons, lessonSlug) => {
    if (typeof currentLessons !== 'undefined') {
      return (
        currentLessons[parseInt(lessonSlug, 10) - 1] &&
        currentLessons[parseInt(lessonSlug, 10) - 1]
      );
    }
  }
);

const getNextTopic = createSelector(
  getLessonsForCurrentModule,
  getCurrentLessonSlug,
  getCurrentTopicSlug,
  (currentLessons, lessonSlug, topicSlug) => {
    if (typeof currentLessons !== 'undefined') {
      return (
        currentLessons[parseInt(lessonSlug, 10) - 1] &&
        currentLessons[parseInt(lessonSlug, 10) - 1].topics[
          parseInt(topicSlug, 10) + 1
        ]
      );
    }
  }
);

const getNextStep = createSelector(
  getDischargeSteps,
  getCurrentModuleSlug,
  getCurrentTopicSlug, 
  (currentLessons, lessonSlug, topicSlug) => {
    if (typeof currentLessons !== 'undefined') {
      const lesson = currentLessons[parseInt(lessonSlug, 10) - 1];
      if (typeof lesson !== 'undefined') {
        return lesson.topics[parseInt(topicSlug, 10) + 1] || null;
      }
    }
    return null;
  }
);

const getPrevTopic = createSelector(
  getLessonsForCurrentModule,
  getCurrentLessonSlug,
  getCurrentTopicSlug,
  (currentLessons, lessonSlug, topicSlug) => {
    const lessonIndex = parseInt(lessonSlug, 10) - 1;
    if (typeof currentLessons !== 'undefined') {
      const topicIndex = parseInt(topicSlug, 10) - 1;
      if (lessonIndex > 0 && topicIndex === -1) {
        const prevLessonLastTopicIndex =
          currentLessons[lessonIndex - 1].topics.length - 1;
        return (
          currentLessons[lessonIndex - 1] &&
          currentLessons[lessonIndex - 1].topics[prevLessonLastTopicIndex]
        ) || null;
      }
      return (
        currentLessons[lessonIndex] &&
        currentLessons[lessonIndex].topics[topicIndex]
      ) || null;
    }
    return null;
  }
);

const getPrevStep = createSelector(
  getDischargeSteps,
  getCurrentModuleSlug,
  getCurrentTopicSlug, 
  (currentLessons, lessonSlug, topicSlug) => {
    if (typeof currentLessons !== 'undefined') {
      const lessonIndex = parseInt(lessonSlug, 10) - 1;
      if (typeof currentLessons[lessonIndex] !== 'undefined') {
        const topicIndex = parseInt(topicSlug, 10) - 1;
        let result = currentLessons[lessonIndex].topics[topicIndex];
        if (!result && lessonIndex > 0) {
          const prevStepLastTopicIndex =
            currentLessons[lessonIndex - 1].topics.length - 1;
          result = currentLessons[lessonIndex - 1].topics[prevStepLastTopicIndex];
        }
        return result || null;
      }
    }
    return null;
  }
);

const getFirstLessonForCurrentModule = createSelector(
  getLessonsForCurrentModule,
  lessons => _.find(lessons, lesson => lesson.displayOrder === 1) || null
);

const getLessonIdToPageCountMap = createSelector(
  getLessons,
  lessons => _.mapValues(lessons, lesson => lesson.pages.length)
);

const getDischargeLessons = createSelector(
  getLessons,
  getDischargeModuleForCurrentLocale,
  (allLessons, dischargeModule) =>
    _.chain(allLessons)
      .filter(lesson => lesson.moduleId === dischargeModule.id)
      .sortBy('displayOrder')
      .value()
);

export {
  getLessonsWithOrderedPages,
  getLessonSlugToLessonMap,
  getCurrentLesson,
  getNextTopic,
  getPrevTopic,
  getLessonsForCurrentModule,
  getFirstLessonForCurrentModule,
  getLessonIdToPageCountMap,
  getDischargeLessons,
  getCurrentDischargeStep,
  getNextStep,
  getPrevStep
};